import React, { useState, useRef, useMemo, useEffect, useContext } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { themeColors } from '../theme';
import PlotExportBar from './PlotExportBar';
import SimProgressBar from './SimProgressBar';
import { AxisInput } from './JVChartPanel';
import { runJob } from '../services/jobClient';
import {
  WorkspaceContext, WorkspaceContextSetter,
} from '../context/WorkspaceContext';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

// ─── Helpers ──────────────────────────────────────────────────────────────

function getNaUniformity(naxData) {
  if (!naxData || naxData.length < 3) return '—';
  const vals = naxData.map(d => d.Na_cm3).filter(v => v > 0);
  if (vals.length < 2) return '—';
  const ratio = Math.max(...vals) / Math.min(...vals);
  if (ratio < 1.5) return 'Uniform';
  if (ratio < 3.0) return 'Slightly graded';
  return 'Strongly graded';
}

// Derive Na(x) from a list of {V, C_pF} via Mott-Schottky.
// Used both for uploaded data and as a sanity check on simulated data.
function computeNaxFromCV(cvPoints, eps_r = 9.4) {
  const q    = 1.602176634e-19;
  const eps0 = 8.854e-14;          // F/cm
  const out  = [];
  for (let i = 0; i < cvPoints.length - 1; i++) {
    const C1 = cvPoints[i].C_pF     * 1e-12;   // F/cm²
    const C2 = cvPoints[i + 1].C_pF * 1e-12;
    const V1 = cvPoints[i].V;
    const V2 = cvPoints[i + 1].V;
    if (C1 <= 0 || C2 <= 0) continue;
    const dV = V2 - V1;
    if (Math.abs(dV) < 1e-6) continue;
    const d_inv_C2 = (1.0 / (C2 * C2) - 1.0 / (C1 * C1)) / dV;
    if (Math.abs(d_inv_C2) < 1e-30) continue;
    const Na   = Math.abs(2.0 / (q * eps0 * eps_r * d_inv_C2));
    const x_cm = eps0 * eps_r / (0.5 * (C1 + C2));
    const x_nm = x_cm * 1e7;
    if (Na > 0 && Na < 1e20 && x_nm > 0 && x_nm < 5000) {
      out.push({ x_nm, Na_cm3: Na });
    }
  }
  out.sort((a, b) => a.x_nm - b.x_nm);
  return out;
}

function estimateVbiFromCV(cvPoints) {
  if (!cvPoints || cvPoints.length < 4) return null;
  const V  = cvPoints.map(d => d.V);
  const Y  = cvPoints.map(d => 1.0 / Math.pow(d.C_pF * 1e-12, 2)).filter(Number.isFinite);
  const Vv = V.slice(0, Y.length);
  const n = Y.length;
  if (n < 4) return null;
  const sumX = Vv.reduce((a, b) => a + b, 0);
  const sumY = Y.reduce((a, b) => a + b, 0);
  const sumXY = Vv.reduce((a, b, i) => a + b * Y[i], 0);
  const sumXX = Vv.reduce((a, b) => a + b * b, 0);
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  if (Math.abs(slope) < 1e-30) return null;
  const Vbi = -intercept / slope;
  return Number.isFinite(Vbi) && Vbi > 0.3 && Vbi < 2.0 ? Vbi : null;
}

function avgNa(naxData) {
  if (!naxData || naxData.length === 0) return null;
  const vals = naxData.map(d => d.Na_cm3).filter(v => v > 0);
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

// Padded axis domain from data values, per-bound manual override wins.
// `manual` = { min, max } where null means "auto". Linear pads by 5% of
// the span; log pads multiplicatively (÷/×1.2) and ignores non-positive
// values (a log axis cannot render them — a non-positive manual bound
// falls back to auto for that bound).
function paddedDomain(vals, manual, log = false, pad = 0.05) {
  const fin = vals.filter(v => Number.isFinite(v) && (!log || v > 0));
  let lo = 'auto', hi = 'auto';
  if (fin.length > 0) {
    const dLo = Math.min(...fin);
    const dHi = Math.max(...fin);
    if (log) {
      lo = dLo / 1.2;
      hi = dHi * 1.2;
    } else {
      const span = (dHi - dLo) || Math.abs(dHi) || 1;
      lo = dLo - span * pad;
      hi = dHi + span * pad;
    }
  }
  const mLo = manual?.min, mHi = manual?.max;
  return [
    mLo != null && !(log && mLo <= 0) ? mLo : lo,
    mHi != null && !(log && mHi <= 0) ? mHi : hi,
  ];
}

// FastAPI validation errors (422) arrive as an array of {loc, msg, type}
// objects — stringify them readably instead of rendering "[object Object]".
function formatApiError(e) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d =>
      d && typeof d === 'object'
        ? `${Array.isArray(d.loc) ? d.loc.join('.') + ': ' : ''}${d.msg || JSON.stringify(d)}`
        : String(d)
    ).join('; ');
  }
  if (typeof detail === 'string') return detail;
  if (detail != null) return JSON.stringify(detail);
  return e?.message || 'Request failed';
}

function wAtZero(cvPoints) {
  if (!cvPoints || cvPoints.length === 0) return null;
  // Pick the point closest to V=0 with a defined W
  let best = null;
  for (const d of cvPoints) {
    if (d.W_nm == null) continue;
    if (best == null || Math.abs(d.V) < Math.abs(best.V)) best = d;
  }
  return best?.W_nm ?? null;
}

// ─── Component ────────────────────────────────────────────────────────────

export default function CVPanel({ results, theme = 'dark' }) {
  const c = themeColors(theme);
  const devicePayload = results?._lastPayload || null;

  const [activeSubTab, setActiveSubTab] = useState('simulated');
  const [vStart,    setVStart]    = useState(-2.0);
  const [vEnd,      setVEnd]      = useState(0.5);
  const [nPoints,   setNPoints]   = useState(50);
  const [isCalculating, setIsCalculating] = useState(false);
  // Real per-bias-point progress polled from the async job endpoint
  // ({current, total, stage} | null until the first poll returns).
  const [cvProgress, setCvProgress] = useState(null);
  const [cvResult,  setCvResult]  = useState(null);   // full server response
  const [cvData,    setCvData]    = useState([]);
  const [naxData,   setNaxData]   = useState([]);
  const [error,     setError]     = useState(null);

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedCV,   setUploadedCV]   = useState([]);
  const [uploadedNax,  setUploadedNax]  = useState([]);

  const [aiAnalysis,    setAiAnalysis]    = useState('');
  const [aiBusy,        setAiBusy]        = useState(false);

  // ── Plot scale controls (2026-07) ─────────────────────────────────
  // null = autoscale for that bound; a number = manual override, which
  // wins until cleared (blank/auto) or a new C-V simulation completes
  // (calculateCV resets all four back to autoscale).
  const [cvAxisX,  setCvAxisX]  = useState({ min: null, max: null });
  const [cvAxisY,  setCvAxisY]  = useState({ min: null, max: null });
  const [naxAxisX, setNaxAxisX] = useState({ min: null, max: null });
  const [naxAxisY, setNaxAxisY] = useState({ min: null, max: null });
  const [naxLogY,  setNaxLogY]  = useState(true);

  function resetAxes() {
    setCvAxisX({ min: null, max: null });
    setCvAxisY({ min: null, max: null });
    setNaxAxisX({ min: null, max: null });
    setNaxAxisY({ min: null, max: null });
  }

  const fileRef = useRef(null);

  // Read-side workspace snapshot — analyzeCV posts it as active_context
  // the same way PersistentAI.handleSend does, so the backend context
  // formatter sees the published 'cv' tab slice.
  const workspace = useContext(WorkspaceContext);

  // ── Stage 2: publish to WorkspaceContext (cv block) ───────────────
  const publish = useContext(WorkspaceContextSetter);
  useEffect(() => {
    const fitSummary = cvResult ? {
      Vbi:        cvResult.Vbi        ?? 0,
      W_zero_nm:  cvResult.W_zero_nm  ?? 0,
      Na_avg_cm3: cvResult.Na_avg_cm3 ?? 0,
    } : null;
    publish('cv', {
      active_subtab: activeSubTab,
      v_start: vStart,
      v_end: vEnd,
      n_points: nPoints,
      is_calculating: isCalculating,
      cv_points_count: (activeSubTab === 'uploaded' ? uploadedCV : cvData).length,
      nax_points_count: (activeSubTab === 'uploaded' ? uploadedNax : naxData).length,
      cv_inline:  null,   // Stage 3 enforces < 200-point inline rule
      nax_inline: null,
      uploaded_filename: uploadedFile?.name || null,
      error,
      fit_summary: fitSummary,
    });
  }, [publish, activeSubTab, vStart, vEnd, nPoints, isCalculating,
      cvData, naxData, uploadedCV, uploadedNax,
      uploadedFile, error, cvResult]);

  // Device Na for reference line — try absorber_layers, then layers[].Na
  const deviceNa = useMemo(() => {
    if (!devicePayload) return null;
    const abs = devicePayload.absorber_layers;
    if (abs && abs.length) {
      const Na = abs[0]?.Na;
      if (Na > 0) return Number(Na);
    }
    const flat = devicePayload.layers || [];
    const found = flat.find(l => l.Na > 0 && l.Eg && l.Eg < 2.0);
    return found ? Number(found.Na) : null;
  }, [devicePayload]);

  // Uploaded summary stats
  const uploadedVbi   = useMemo(() => estimateVbiFromCV(uploadedCV), [uploadedCV]);
  const uploadedNaAvg = useMemo(() => avgNa(uploadedNax),            [uploadedNax]);
  const uploadedWzero = useMemo(() => wAtZero(uploadedCV),           [uploadedCV]);

  // ── Handlers ────────────────────────────────────────────────────────
  async function calculateCV() {
    if (!devicePayload) return;
    setIsCalculating(true);
    setCvProgress(null);
    setError(null);
    setCvData([]); setNaxData([]); setCvResult(null);
    try {
      // Async job flow (V1.0): enqueue + poll — survives HTTP timeouts on
      // long sweeps and reports REAL per-bias-point progress. The result
      // body matches the old synchronous /simulation/cv response.
      const data = await runJob('cv', {
        device:   devicePayload,
        v_start:  Number(vStart),
        v_end:    Number(vEnd),
        n_points: Number(nPoints),
      }, { onProgress: (p) => setCvProgress(p) });
      if (data?.success === false) {
        setError(data.error || 'CV simulation failed');
      }
      setCvResult(data);
      setCvData(data?.cv_data || []);
      setNaxData(data?.nax_data || []);
      // New simulation → back to autoscale (manual overrides cleared).
      resetAxes();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setIsCalculating(false);
    }
  }

  function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target.result || '');
      const parsed = [];
      for (const raw of text.split(/\r?\n/)) {
        const line = raw.trim();
        if (!line) continue;
        if (line.startsWith('#') || line.startsWith('%')) continue;
        const parts = line.split(/[\s,;\t]+/).filter(Boolean);
        if (parts.length < 2) continue;
        const V = parseFloat(parts[0]);
        let   C = parseFloat(parts[1]);
        if (!Number.isFinite(V) || !Number.isFinite(C)) continue;
        // Auto-detect unit: if |C| < 1e-6 assume F → convert to pF.
        if (Math.abs(C) < 1e-6) C *= 1e12;
        parsed.push({ V, C_pF: C, W_nm: null });
      }
      if (parsed.length < 4) {
        setError('Uploaded file: need ≥ 4 numeric (V, C) rows.');
        setUploadedCV([]); setUploadedNax([]);
        return;
      }
      parsed.sort((a, b) => a.V - b.V);
      setUploadedCV(parsed);
      setUploadedNax(computeNaxFromCV(parsed));
      setError(null);
    };
    reader.readAsText(file);
  }

  function clearUpload() {
    setUploadedFile(null); setUploadedCV([]); setUploadedNax([]);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function analyzeCV() {
    if (!cvResult && uploadedCV.length === 0) return;
    setAiBusy(true);
    setAiAnalysis('');
    try {
      const ctx = {
        simulated: cvResult ? {
          Vbi: cvResult.Vbi,
          W_zero_nm: cvResult.W_zero_nm,
          Na_avg_cm3: cvResult.Na_avg_cm3,
          n_points: cvResult.n_points,
          uniformity: getNaUniformity(naxData),
        } : null,
        measured: uploadedCV.length > 0 ? {
          n_points: uploadedCV.length,
          Vbi: uploadedVbi,
          W_zero_nm: uploadedWzero,
          Na_avg: uploadedNaAvg,
          uniformity: getNaUniformity(uploadedNax),
        } : null,
        device_Na: deviceNa,
      };
      // ChatRequest V2 (backend/app/schemas/ai_schemas.py): the legacy
      // {message, context, history} shape 422s — session_metadata is
      // REQUIRED and the workspace snapshot goes in active_context.
      // Same snapshot shape as PersistentAI.handleSend, so the backend
      // context formatter reads the published 'cv' tab slice; the
      // extracted C-V numbers stay inline in the message.
      const activeContextSnapshot = workspace ? {
        tab:                'cv',
        tab_state:          workspace.tab_state || {},
        device_spec:        workspace.device_spec || null,
        sim_settings:       workspace.sim_settings || null,
        latest_simulation:  workspace.latest_simulation || null,
        simulation_history: workspace.simulation_history || [],
        ui:                 workspace.ui || null,
      } : { tab: 'cv', tab_state: {} };

      const { data } = await axios.post(`${API}/ai/chat`, {
        message:
          `Analyze these C-V results and the Na(x) doping profile. ` +
          `What do they say about device quality and which simulation ` +
          `parameters should be adjusted? Data: ${JSON.stringify(ctx)}`,
        conversation_history: [],
        active_context: activeContextSnapshot,
        session_metadata: {
          conversation_id:
            `cv-${Date.now()}-${Math.random().toString(36).slice(2)}`,
          started_at: new Date().toISOString(),
          message_count: 0,
        },
      });
      // V2 responses carry the text in `message` (`response` kept as a
      // legacy fallback).
      setAiAnalysis(data?.message || data?.response || '(no response)');
    } catch (e) {
      setAiAnalysis(`Error: ${formatApiError(e)}`);
    } finally {
      setAiBusy(false);
    }
  }

  // ── Styles ─────────────────────────────────────────────────────────
  const inputStyle = {
    padding: '5px 8px', fontSize: 13,
    background: c.input, color: c.inputText,
    border: `1px solid ${c.border}`, borderRadius: 4,
    fontFamily: 'IBM Plex Mono, monospace',
  };
  const labelStyle = {
    fontSize: 13, color: c.textMuted,
    fontFamily: 'IBM Plex Mono, monospace',
    display: 'flex', alignItems: 'center', gap: 6,
  };
  const btnPrimary = (disabled) => ({
    padding: '6px 14px', fontSize: 13,
    background: disabled ? c.btnInactive : c.btnPrimary,
    color: disabled ? c.btnInactiveText : c.btnPrimaryText,
    border: 'none', borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'IBM Plex Mono, monospace',
  });
  const btnSecondary = {
    padding: '6px 14px', fontSize: 13,
    background: c.btnSecondary, color: c.btnSecondaryText,
    border: `1px solid ${c.border}`, borderRadius: 4,
    cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace',
  };
  const subtab = (active) => ({
    padding: '5px 14px',
    background: active ? c.btnActive : c.btnInactive,
    color:      active ? c.btnPrimaryText : c.btnInactiveText,
    border: `1px solid ${active ? c.btnActive : c.border}`,
    borderRadius: 4, cursor: 'pointer',
    fontFamily: 'IBM Plex Mono, monospace', fontSize: 13,
  });

  // ── CSV export rows ────────────────────────────────────────────────
  const cvCSV = useMemo(() => {
    if (cvData.length === 0 && uploadedCV.length === 0) return [];
    const rows = [];
    cvData.forEach(d => rows.push({ source: 'simulated',
      V: d.V, C_pF: d.C_pF, W_nm: d.W_nm }));
    uploadedCV.forEach(d => rows.push({ source: 'measured',
      V: d.V, C_pF: d.C_pF, W_nm: d.W_nm ?? '' }));
    return rows;
  }, [cvData, uploadedCV]);

  const naxCSV = useMemo(() => {
    if (naxData.length === 0 && uploadedNax.length === 0) return [];
    const rows = [];
    naxData.forEach(d => rows.push({ source: 'simulated',
      x_nm: d.x_nm, Na_cm3: d.Na_cm3 }));
    uploadedNax.forEach(d => rows.push({ source: 'measured',
      x_nm: d.x_nm, Na_cm3: d.Na_cm3 }));
    return rows;
  }, [naxData, uploadedNax]);

  // ── Axis domains: data-driven autoscale (5% pad), manual wins ─────
  const cvXDomain = useMemo(() =>
    paddedDomain([...cvData, ...uploadedCV].map(d => d.V), cvAxisX),
    [cvData, uploadedCV, cvAxisX]);
  const cvYDomain = useMemo(() =>
    paddedDomain([...cvData, ...uploadedCV].map(d => d.C_pF), cvAxisY),
    [cvData, uploadedCV, cvAxisY]);
  const naxXDomain = useMemo(() =>
    paddedDomain([...naxData, ...uploadedNax].map(d => d.x_nm), naxAxisX),
    [naxData, uploadedNax, naxAxisX]);
  const naxYDomain = useMemo(() =>
    paddedDomain([...naxData, ...uploadedNax].map(d => d.Na_cm3),
                 naxAxisY, naxLogY),
    [naxData, uploadedNax, naxAxisY, naxLogY]);

  const axisRow = {
    display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
    marginBottom: 6, fontSize: 12, color: c.textMuted,
    fontFamily: 'IBM Plex Mono, monospace',
  };
  const axisResetBtn = {
    background: c.btnSecondary, color: c.btnSecondaryText,
    border: `1px solid ${c.border}`, borderRadius: 4,
    padding: '3px 10px', fontSize: 12, cursor: 'pointer',
    fontFamily: 'IBM Plex Mono, monospace',
  };
  const scaleBtn = (active) => ({
    ...axisResetBtn, padding: '3px 8px',
    background: active ? c.btnActive : c.btnSecondary,
    color:      active ? c.btnPrimaryText : c.btnSecondaryText,
    borderColor: active ? c.btnActive : c.border,
  });

  // ── Render ─────────────────────────────────────────────────────────
  const haveAny = cvData.length > 0 || uploadedCV.length > 0;

  return (
    <div style={{
      padding: 20, color: c.text, background: c.bg,
      height: '100%', overflowY: 'auto',
      fontFamily: 'IBM Plex Mono, monospace',
    }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 16, color: c.accentDark,
          letterSpacing: 1 }}>
          Capacitance-Voltage (C-V) &amp; Doping Profile Na(x)
        </h2>
        <div style={{ fontSize: 13, color: c.textMuted,
          marginTop: 4, lineHeight: 1.5 }}>
          Sweeps the dark DD solver across DC bias, extracts depletion
          width, and derives Na(x) by Mott-Schottky.  Compare to a
          measured C-V file alongside.
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <button style={subtab(activeSubTab === 'simulated')}
          onClick={() => setActiveSubTab('simulated')}>Simulated</button>
        <button style={subtab(activeSubTab === 'uploaded')}
          onClick={() => setActiveSubTab('uploaded')}>Uploaded</button>
      </div>

      {/* Controls */}
      {activeSubTab === 'simulated' && (
        <div style={{
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
          marginBottom: 14,
        }}>
          <label style={labelStyle}>V start (V):
            <input type="number" value={vStart} step={0.1}
              onChange={e => setVStart(parseFloat(e.target.value) || 0)}
              style={{ ...inputStyle, width: 80 }} />
          </label>
          <label style={labelStyle}>V end (V):
            <input type="number" value={vEnd} step={0.1}
              onChange={e => setVEnd(parseFloat(e.target.value) || 0)}
              style={{ ...inputStyle, width: 80 }} />
          </label>
          <label style={labelStyle}>Points:
            <input type="number" value={nPoints} min={10} max={200}
              onChange={e => setNPoints(parseInt(e.target.value) || 50)}
              style={{ ...inputStyle, width: 70 }} />
          </label>
          <button style={btnPrimary(isCalculating || !devicePayload)}
            disabled={isCalculating || !devicePayload}
            onClick={calculateCV}>
            {isCalculating ? '⏳ Calculating...' : '▶ Calculate C-V'}
          </button>
          {!devicePayload && (
            <span style={{ fontSize: 12, color: c.textMuted }}>
              Run a simulation first — C-V uses the last submitted device.
            </span>
          )}
        </div>
      )}

      {activeSubTab === 'uploaded' && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
          marginBottom: 14,
        }}>
          <button style={btnSecondary}
            onClick={() => fileRef.current?.click()}>
            📂 Upload C-V Data (CSV)
          </button>
          <input ref={fileRef} type="file" accept=".csv,.txt"
            hidden onChange={handleUpload} />
          <span style={{ fontSize: 12, color: c.textMuted }}>
            Format: V(V), C(F or pF) — 2 columns (auto unit detect)
          </span>
          {uploadedFile && (
            <span style={{ fontSize: 12, color: c.accent,
              display: 'flex', alignItems: 'center', gap: 4 }}>
              ✓ {uploadedFile.name} — {uploadedCV.length} points
              <button onClick={clearUpload} style={{
                background: 'none', border: 'none', color: c.red,
                cursor: 'pointer', fontSize: 14, padding: 0,
              }}>×</button>
            </span>
          )}
        </div>
      )}

      {/* Progress — one dark solve per bias point. REAL counts polled
          from the job endpoint once available; time-estimated until then.
          SimProgressBar owns its timer; cleared on completion/error/unmount. */}
      <SimProgressBar
        active={isCalculating}
        totalPoints={Number(nPoints) || 0}
        label="Simulating C-V"
        c={c}
        progress={cvProgress}
      />

      {error && (
        <div style={{ padding: 10, marginBottom: 12,
          border: `1px solid ${c.red}`, color: c.red,
          background: theme === 'dark'
            ? 'rgba(248,113,113,0.05)' : 'rgba(220,53,69,0.08)',
          fontSize: 13, borderRadius: 4 }}>
          {error}
        </div>
      )}

      {/* Plots */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: 16, marginTop: 4,
      }}>
        {/* C-V plot */}
        <div>
          <div style={{ fontSize: 13, color: c.textMuted,
            marginBottom: 4, letterSpacing: 1, textTransform: 'uppercase' }}>
            Capacitance-Voltage
          </div>
          <div style={axisRow}>
            <span>V:</span>
            <AxisInput c={c} value={cvAxisX.min} placeholder="auto" step={0.5}
              onChange={v => setCvAxisX(a => ({ ...a, min: v }))} />
            <span>→</span>
            <AxisInput c={c} value={cvAxisX.max} placeholder="auto" step={0.5}
              onChange={v => setCvAxisX(a => ({ ...a, max: v }))} />
            <span style={{ marginLeft: 6 }}>C:</span>
            <AxisInput c={c} value={cvAxisY.min} placeholder="auto" step={100}
              onChange={v => setCvAxisY(a => ({ ...a, min: v }))} />
            <span>→</span>
            <AxisInput c={c} value={cvAxisY.max} placeholder="auto" step={100}
              onChange={v => setCvAxisY(a => ({ ...a, max: v }))} />
            <button style={axisResetBtn} onClick={() => {
              setCvAxisX({ min: null, max: null });
              setCvAxisY({ min: null, max: null });
            }}>Reset</button>
          </div>
          <div id="cv-chart" style={{
            width: '100%', height: 320, minHeight: 320, minWidth: 200,
            position: 'relative',
            background: c.plotBg, border: `1px solid ${c.border}`,
            padding: 10, borderRadius: 6,
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid} />
                <XAxis dataKey="V" type="number"
                  domain={cvXDomain} allowDataOverflow
                  tick={{ fill: c.plotText, fontSize: 11,
                    fontFamily: 'IBM Plex Mono' }}
                  label={{ value: 'Voltage (V)', position: 'insideBottom',
                    offset: -8, fill: c.plotText, fontSize: 12,
                    fontFamily: 'IBM Plex Mono' }} />
                <YAxis dataKey="C_pF"
                  domain={cvYDomain} allowDataOverflow
                  tick={{ fill: c.plotText, fontSize: 11,
                    fontFamily: 'IBM Plex Mono' }}
                  tickFormatter={v => Math.abs(Number(v)) >= 1e4
                    ? Number(v).toExponential(1)
                    : String(Number(Number(v).toPrecision(6)))}
                  label={{ value: 'C (pF/cm²)', angle: -90,
                    position: 'insideLeft', offset: -4,
                    textAnchor: 'middle', fill: c.plotText, fontSize: 12,
                    fontFamily: 'IBM Plex Mono' }} />
                <Tooltip contentStyle={{
                  background: c.bgCard, border: `1px solid ${c.border}`,
                }} />
                <Legend verticalAlign="top" align="right"
                  wrapperStyle={{ paddingBottom: 0, top: 0 }} />
                {cvData.length > 0 && (
                  <Line data={cvData} dataKey="C_pF" stroke={c.accent}
                    strokeWidth={2} dot={false} name="Simulated"
                    isAnimationActive={false} />
                )}
                {uploadedCV.length > 0 && (
                  <Line data={uploadedCV} dataKey="C_pF" stroke="#ffd700"
                    strokeWidth={2} dot={false}
                    strokeDasharray="5 5" name="Measured"
                    isAnimationActive={false} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <PlotExportBar
            chartId="cv-chart"
            csvData={cvCSV}
            csvFilename="cv_curve"
            plotFilename="cv"
            c={c}
            disabled={cvCSV.length === 0}
            delayMs={100} />
        </div>

        {/* Na(x) plot */}
        <div>
          <div style={{ fontSize: 13, color: c.textMuted,
            marginBottom: 4, letterSpacing: 1, textTransform: 'uppercase' }}>
            Doping Profile Na(x) — Mott-Schottky
          </div>
          <div style={axisRow}>
            <span>x:</span>
            <AxisInput c={c} value={naxAxisX.min} placeholder="auto" step={100}
              onChange={v => setNaxAxisX(a => ({ ...a, min: v }))} />
            <span>→</span>
            <AxisInput c={c} value={naxAxisX.max} placeholder="auto" step={100}
              onChange={v => setNaxAxisX(a => ({ ...a, max: v }))} />
            <span style={{ marginLeft: 6 }}>Na:</span>
            <AxisInput c={c} value={naxAxisY.min} placeholder="auto" step={1e14}
              onChange={v => setNaxAxisY(a => ({ ...a, min: v }))} />
            <span>→</span>
            <AxisInput c={c} value={naxAxisY.max} placeholder="auto" step={1e14}
              onChange={v => setNaxAxisY(a => ({ ...a, max: v }))} />
            <button style={scaleBtn(naxLogY)}
              onClick={() => setNaxLogY(true)}>Log</button>
            <button style={scaleBtn(!naxLogY)}
              onClick={() => setNaxLogY(false)}>Linear</button>
            <button style={axisResetBtn} onClick={() => {
              setNaxAxisX({ min: null, max: null });
              setNaxAxisY({ min: null, max: null });
            }}>Reset</button>
          </div>
          <div id="nax-chart" style={{
            width: '100%', height: 320, minHeight: 320, minWidth: 200,
            position: 'relative',
            background: c.plotBg, border: `1px solid ${c.border}`,
            padding: 10, borderRadius: 6,
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.plotGrid} />
                <XAxis dataKey="x_nm" type="number"
                  domain={naxXDomain} allowDataOverflow
                  tick={{ fill: c.plotText, fontSize: 11,
                    fontFamily: 'IBM Plex Mono' }}
                  label={{ value: 'Depletion depth (nm)',
                    position: 'insideBottom', offset: -8,
                    fill: c.plotText, fontSize: 12,
                    fontFamily: 'IBM Plex Mono' }} />
                <YAxis dataKey="Na_cm3" scale={naxLogY ? 'log' : 'linear'}
                  domain={naxYDomain} allowDataOverflow
                  tick={{ fill: c.plotText, fontSize: 11,
                    fontFamily: 'IBM Plex Mono' }}
                  tickFormatter={v => Number(v).toExponential(0)}
                  label={{ value: 'Na (cm⁻³)', angle: -90,
                    position: 'insideLeft', offset: -4,
                    textAnchor: 'middle', fill: c.plotText, fontSize: 12,
                    fontFamily: 'IBM Plex Mono' }} />
                <Tooltip contentStyle={{
                  background: c.bgCard, border: `1px solid ${c.border}`,
                }} formatter={v => [Number(v).toExponential(2), 'Na']} />
                <Legend verticalAlign="top" align="right"
                  wrapperStyle={{ paddingBottom: 0, top: 0 }} />
                {deviceNa && (
                  <ReferenceLine y={deviceNa} stroke="#ff6b6b"
                    strokeDasharray="4 4"
                    label={{
                      value: `Input Na=${Number(deviceNa).toExponential(1)}`,
                      fill: '#ff6b6b', fontSize: 11,
                    }} />
                )}
                {naxData.length > 0 && (
                  <Line data={naxData} dataKey="Na_cm3" stroke={c.accent}
                    strokeWidth={2} dot={false} name="Simulated"
                    isAnimationActive={false} />
                )}
                {uploadedNax.length > 0 && (
                  <Line data={uploadedNax} dataKey="Na_cm3" stroke="#ffd700"
                    strokeWidth={2} dot={false}
                    strokeDasharray="5 5" name="Measured"
                    isAnimationActive={false} />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <PlotExportBar
            chartId="nax-chart"
            csvData={naxCSV}
            csvFilename="nax_profile"
            plotFilename="nax"
            c={c}
            disabled={naxCSV.length === 0}
            delayMs={100} />
        </div>
      </div>

      <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6,
        fontStyle: 'italic' }}>
        Axes autoscale to the data (5% pad). Manual min/max entries win
        until cleared (blank = auto) — a new C-V simulation resets all
        axes back to autoscale.
      </div>

      {/* Extracted-parameters table */}
      {haveAny && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, color: c.textMuted,
            marginBottom: 4, letterSpacing: 1, textTransform: 'uppercase' }}>
            Extracted Parameters
          </div>
          <table style={{
            width: '100%', borderCollapse: 'collapse', fontSize: 13,
            border: `1px solid ${c.border}`, borderRadius: 4,
            overflow: 'hidden',
          }}>
            <thead>
              <tr style={{ background: c.bgSecondary }}>
                <th style={{ padding: '6px 12px', textAlign: 'left',
                  color: c.textMuted, fontWeight: 600 }}>Parameter</th>
                <th style={{ padding: '6px 12px', textAlign: 'right',
                  color: c.textMuted, fontWeight: 600 }}>Simulated</th>
                <th style={{ padding: '6px 12px', textAlign: 'right',
                  color: c.textMuted, fontWeight: 600 }}>Measured</th>
              </tr>
            </thead>
            <tbody>
              <Row c={c} label="Built-in Vbi (V)"
                sim={cvResult?.Vbi != null ? cvResult.Vbi.toFixed(3) : '—'}
                meas={uploadedVbi != null ? uploadedVbi.toFixed(3) : '—'} />
              <Row c={c} stripe label="W at V=0 (nm)"
                sim={cvResult?.W_zero_nm != null
                  ? cvResult.W_zero_nm.toFixed(0) : '—'}
                meas={uploadedWzero != null ? uploadedWzero.toFixed(0) : '—'} />
              <Row c={c} label="Na average (cm⁻³)"
                sim={cvResult?.Na_avg_cm3
                  ? cvResult.Na_avg_cm3.toExponential(2) : '—'}
                meas={uploadedNaAvg
                  ? uploadedNaAvg.toExponential(2) : '—'} />
              <Row c={c} stripe label="Na uniformity"
                sim={getNaUniformity(naxData)}
                meas={getNaUniformity(uploadedNax)} />
            </tbody>
          </table>

          <button onClick={analyzeCV}
            disabled={aiBusy || (!cvResult && uploadedCV.length === 0)}
            style={{
              marginTop: 12, padding: '8px 16px',
              background: aiBusy ? c.btnInactive : c.btnPrimary,
              color: aiBusy ? c.btnInactiveText : c.btnPrimaryText,
              border: 'none', borderRadius: 6,
              cursor: aiBusy ? 'not-allowed' : 'pointer', fontSize: 13,
              fontFamily: 'IBM Plex Mono, monospace',
            }}>
            {aiBusy ? '🤖 Thinking...' : '🤖 AI Analysis of C-V Results'}
          </button>

          {aiAnalysis && (
            <div style={{
              marginTop: 12, padding: 12,
              background: c.bgSecondary, borderRadius: 8,
              border: `1px solid ${c.border}`,
              fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
              color: c.text,
            }}>
              {aiAnalysis}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ c, label, sim, meas, stripe }) {
  return (
    <tr style={{ background: stripe ? c.bgSecondary : 'transparent' }}>
      <td style={{ padding: '6px 12px', color: c.text,
        borderTop: `1px solid ${c.border}` }}>{label}</td>
      <td style={{ padding: '6px 12px', textAlign: 'right', color: c.text,
        borderTop: `1px solid ${c.border}` }}>{sim}</td>
      <td style={{ padding: '6px 12px', textAlign: 'right', color: c.text,
        borderTop: `1px solid ${c.border}` }}>{meas}</td>
    </tr>
  );
}
